import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, DollarSign, Star, Briefcase, UserCheck, Target, Building2, Contact, CheckCircle2, AlertCircle, ArrowRight, Home } from "lucide-react";
import ActivityDashboardWidget from "../components/ActivityDashboardWidget";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [crmStats, setCrmStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const [crewResponse, crmResponse] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/crm/stats/summary")
      ]);
      setStats(crewResponse.data);
      setCrmStats(crmResponse.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Employees",
      value: stats?.total_employees || 0,
      icon: Users,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      description: `${stats?.active_employees || 0} active`,
    },
    {
      title: "Independent Contractors",
      value: stats?.total_contractors || 0,
      icon: Briefcase,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      textColor: "text-purple-600",
      description: "Contract workers",
    },
    {
      title: "Seasoned/Term Employees",
      value: stats?.total_seasoned || 0,
      icon: UserCheck,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      description: "Full-time staff",
    },
    {
      title: "Total Hours Tracked",
      value: stats?.total_hours || 0,
      icon: Clock,
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      textColor: "text-orange-600",
      description: `${stats?.total_time_entries || 0} entries`,
    },
    {
      title: "Total Payments",
      value: `$${(stats?.total_payments || 0).toLocaleString()}`,
      icon: DollarSign,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-600",
      description: "All time",
    },
    // Employee Rating card removed - visible only on Time Entry screen
  ];

  const handleViewCrewTracker = () => {
    // Navigate to employees and sidebar will auto-expand
    navigate('/employees');
  };

  const handleOpenCRM = () => {
    // Navigate to leads and sidebar will auto-expand
    navigate('/leads');
  };

  return (
    <div className="space-y-8 page-enter" data-testid="dashboard-page">
      {/* Welcome Section - At TOP */}
      <div className="space-y-4 fade-in">
        {/* Section Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Home className="h-6 w-6 text-teal-600" />
            <h2 className="text-2xl font-bold text-slate-900">Welcome to Cleanifax</h2>
          </div>
          <p className="text-slate-600 max-w-3xl mx-auto">
            Your unified workspace for managing post-construction operations and client relationships.
          </p>
        </div>

        {/* Dual Panel Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mt-6">
          {/* Project Tracker Card */}
          <Card className="border-2 border-slate-200 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-300 card-slide-up">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg md:text-xl text-slate-900">Project Tracker</CardTitle>
                    <CardDescription className="mt-1 text-xs md:text-sm">
                      Workforce and operations management
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Track independent contractors and full-time staff with complete profiles, contact details, and hire dates.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Log hours worked, calculate payments, and assess performance.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Keep all project and crew records accessible in one place.</span>
                </li>
              </ul>
              <button
                onClick={handleViewCrewTracker}
                className="w-full mt-4 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2 font-medium shadow-sm hover:shadow-md"
              >
                View Project Tracker
                <ArrowRight className="h-4 w-4" />
              </button>
            </CardContent>
          </Card>

          {/* CRM & Business Development Card */}
          <Card className="border-2 border-slate-200 shadow-sm hover:shadow-lg hover:border-teal-300 transition-all duration-300 card-slide-up" style={{ animationDelay: '100ms' }}>
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-teal-100 p-3 rounded-xl">
                    <Briefcase className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg md:text-xl text-slate-900">CRM & Business Development</CardTitle>
                    <CardDescription className="mt-1 text-xs md:text-sm">
                      Client relationships and revenue tracking
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Manage leads, companies, and contacts with comprehensive profiles.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Send quotes, track invoices, and monitor revenue performance.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Schedule follow-ups and sync your calendar for timely client engagement.</span>
                </li>
              </ul>
              <button
                onClick={handleOpenCRM}
                className="w-full mt-4 px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors duration-200 flex items-center justify-center gap-2 font-medium shadow-sm hover:shadow-md"
              >
                Open CRM
                <ArrowRight className="h-4 w-4" />
              </button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dashboard Header - Above Overview Sections */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="text-sm md:text-base text-slate-600 mt-1">
          Overview of your workforce and business development
        </p>
      </div>

      {/* Project Tracker Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-1 w-12 bg-blue-600 rounded"></div>
          <h2 className="text-xl font-bold text-slate-900">Project Tracker Overview</h2>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="stat-card border-slate-200 shadow-sm hover:shadow-md" data-testid={`stat-card-${index}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.bgColor} p-2 rounded-lg`}>
                    <Icon className={`h-5 w-5 ${stat.textColor}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900" data-testid={`stat-value-${index}`}>
                    {stat.value}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* CRM Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-1 w-12 bg-green-600 rounded"></div>
          <h2 className="text-xl font-bold text-slate-900">CRM Overview</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 shadow-sm hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Total Leads
              </CardTitle>
              <div className="bg-blue-50 p-2 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {crmStats?.total_leads || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {crmStats?.new_leads || 0} new this week
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Companies
              </CardTitle>
              <div className="bg-purple-50 p-2 rounded-lg">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {crmStats?.total_companies || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {crmStats?.total_contacts || 0} contacts
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Pipeline Value
              </CardTitle>
              <div className="bg-green-50 p-2 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                ${((crmStats?.total_est_value || 0) / 1000).toFixed(0)}K
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Estimated value
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Tasks Due Today
              </CardTitle>
              <div className="bg-orange-50 p-2 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {crmStats?.tasks_due_today || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Pending tasks
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Lead Pipeline Quick View */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Lead Pipeline Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{crmStats?.qualified_leads || 0}</div>
                <div className="text-xs text-slate-600 mt-1">Qualified</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{crmStats?.quote_sent_leads || 0}</div>
                <div className="text-xs text-slate-600 mt-1">Quote Sent</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{crmStats?.won_leads || 0}</div>
                <div className="text-xs text-slate-600 mt-1">Won</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{crmStats?.lost_leads || 0}</div>
                <div className="text-xs text-slate-600 mt-1">Lost</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed */}
      <ActivityDashboardWidget />
    </div>
  );
};

export default DashboardPage;